/**
 * Browser-kant van de pushmeldingen (ronde E).
 *
 * Dunne laag om de Web Push-API's heen: service worker registreren,
 * abonneren met de publieke VAPID-sleutel en de vertaling van een
 * `PushSubscription` naar onze API-invoer. De UI (instellingenblok op
 * de profielpagina) en de server-API leven elders.
 */

import { pushApi } from "@/lib/api";
import type { PushAbonnementInvoer, PushMeldingSoort } from "@/types/api";

export const SW_PAD = "/sw.js";

/** Kan deze browser überhaupt web push? (iOS: pas 16.4+, en alleen
 * als PWA op het beginscherm — zie `draaitStandalone`.) */
export function pushWordtOndersteund(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Draait het portaal als geïnstalleerde app (beginscherm/PWA)? */
export function draaitStandalone(): boolean {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    nav.standalone === true
  );
}

export function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPadOS meldt zich sinds 13 als "Macintosh" met touch.
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
}

/** Kort, herkenbaar apparaatlabel voor het instellingenblok. */
export function apparaatLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1))
    return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  return "Dit apparaat";
}

export async function registreerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(SW_PAD);
  } catch {
    // Geen SW = geen push; de rest van het portaal werkt gewoon.
    return null;
  }
}

/** VAPID-sleutel (base64url) → het BufferSource-formaat van subscribe(). */
export function urlBase64NaarUint8Array(base64url: string): Uint8Array {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rauw = window.atob(base64);
  return Uint8Array.from(rauw, (teken) => teken.charCodeAt(0));
}

/** Het bestaande push-abonnement van deze browser, of null. */
export async function huidigBrowserAbonnement(): Promise<PushSubscription | null> {
  if (!pushWordtOndersteund()) return null;
  const registratie = await navigator.serviceWorker.getRegistration(SW_PAD);
  if (!registratie) return null;
  return registratie.pushManager.getSubscription();
}

/**
 * De publieke sleutel waarmee dit abonnement ooit is gemaakt, of null als
 * de browser hem niet prijsgeeft (sommige WebKit-versies kennen
 * `options.applicationServerKey` niet).
 */
export function sleutelVanAbonnement(
  abonnement: PushSubscription,
): Uint8Array | null {
  const sleutel = (
    abonnement as PushSubscription & {
      options?: { applicationServerKey?: ArrayBuffer | null };
    }
  ).options?.applicationServerKey;
  if (!sleutel || sleutel.byteLength === 0) return null;
  return new Uint8Array(sleutel);
}

/**
 * Hoort dit abonnement bij `publiekeSleutel` (base64url)? Bytegewijs
 * vergeleken. `null` = niet te zeggen (sleutel onbekend in de browser).
 */
export function zelfdeSleutel(
  abonnement: PushSubscription,
  publiekeSleutel: string,
): boolean | null {
  const oud = sleutelVanAbonnement(abonnement);
  if (!oud) return null;
  const huidig = urlBase64NaarUint8Array(publiekeSleutel);
  if (oud.length !== huidig.length) return false;
  for (let i = 0; i < oud.length; i++) {
    if (oud[i] !== huidig[i]) return false;
  }
  return true;
}

export interface AbonneerOpties {
  /** Vraag zo nodig om toestemming (alleen na een klik). Standaard aan. */
  vraagToestemming?: boolean;
  /** Ook bij dezelfde sleutel een vers abonnement (oud endpoint is dood). */
  vervang?: boolean;
}

interface AbonneerResultaat {
  abonnement: PushSubscription;
  /** Endpoint van het vervangen browserabonnement, of null. */
  oudEndpoint: string | null;
}

/**
 * Browserkant van het abonneren, zonder serveraanroepen. Hergebruikt een
 * abonnement met dezelfde sleutel; anders gaat het oude eerst weg in de
 * browser (anders weigert `subscribe()` met InvalidStateError) en komt er
 * een nieuw met `publiekeSleutel`. De serverrij van het oude endpoint
 * blijft staan: de aanroeper meldt die pas af als het nieuwe er is.
 */
async function abonneerInBrowser(
  publiekeSleutel: string,
  opties: AbonneerOpties,
): Promise<AbonneerResultaat> {
  const { vraagToestemming = true, vervang = false } = opties;
  const permissie = vraagToestemming
    ? await Notification.requestPermission()
    : Notification.permission;
  if (permissie !== "granted") {
    throw new Error("permissie_geweigerd");
  }
  const registratie =
    (await navigator.serviceWorker.getRegistration(SW_PAD)) ??
    (await registreerServiceWorker());
  if (!registratie) {
    throw new Error("service_worker_ontbreekt");
  }
  await navigator.serviceWorker.ready;
  const bestaand = await registratie.pushManager.getSubscription();
  if (bestaand) {
    if (!vervang && zelfdeSleutel(bestaand, publiekeSleutel) === true) {
      return { abonnement: bestaand, oudEndpoint: null };
    }
    await bestaand.unsubscribe();
  }
  const abonnement = await registratie.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64NaarUint8Array(
      publiekeSleutel,
    ) as BufferSource,
  });
  const oudEndpoint =
    bestaand && bestaand.endpoint !== abonnement.endpoint
      ? bestaand.endpoint
      : null;
  return { abonnement, oudEndpoint };
}

/** Oude serverrij weg; een serverfout is geen stop. */
async function meldOudEndpointAf(endpoint: string | null): Promise<void> {
  if (!endpoint) return;
  await pushApi.afmelden(endpoint).catch(() => undefined);
}

/**
 * Abonneer deze browser (handmatig, vanaf Profiel).
 *
 * Sessie 202610234: kijk eerst of er al een abonnement is. Hoort dat bij
 * de huidige publieke sleutel, dan hergebruiken we het (de aanroeper
 * registreert het daarna bij de server). Hoort het bij een andere of
 * onbekende sleutel, dan gaat het eerst weg in de browser; de oude
 * serverrij melden we pas af als het nieuwe abonnement er is.
 */
export async function abonneerBrowser(
  publiekeSleutel: string,
  opties: AbonneerOpties = {},
): Promise<PushSubscription> {
  const { abonnement, oudEndpoint } = await abonneerInBrowser(
    publiekeSleutel,
    opties,
  );
  await meldOudEndpointAf(oudEndpoint);
  return abonnement;
}

export type HerstelReden =
  | "sleutel_gewisseld"
  | "onbekend_bij_server"
  | "verlopen_bij_server";

export type HerstelUitkomst =
  | { uitkomst: "overgeslagen"; waarom: string }
  | { uitkomst: "in_orde" }
  | { uitkomst: "hersteld"; reden: HerstelReden };

/**
 * Voorkeuren voor een toestel dat de server niet (meer) kent. Bewust
 * leeg: de server telt een ontbrekende soort als AAN (`wil_soort` in
 * app/services/web_push.py), net als bij aanzetten op Profiel. Er bestaat
 * geen aparte standaardlijst in de frontend of de API.
 */
const STANDAARD_VOORKEUREN: Partial<Record<PushMeldingSoort, boolean>> = {};

async function herstelEenKeer(): Promise<HerstelUitkomst> {
  if (!pushWordtOndersteund()) {
    return { uitkomst: "overgeslagen", waarom: "niet_ondersteund" };
  }
  if (Notification.permission !== "granted") {
    return { uitkomst: "overgeslagen", waarom: "geen_toestemming" };
  }
  const browserAbo = await huidigBrowserAbonnement();
  if (!browserAbo) {
    return { uitkomst: "overgeslagen", waarom: "geen_abonnement" };
  }
  const config = await pushApi.config();
  if (!config.actief || !config.publieke_sleutel) {
    return { uitkomst: "overgeslagen", waarom: "niet_ingericht" };
  }

  // Eerst de serverstand, vóór elke vervanging: kent de server dit
  // endpoint bij een ander account (gedeeld toestel), dan niets afmelden
  // en niets overnemen.
  const server = await pushApi.opvragen(browserAbo.endpoint);
  let verlopen = false;
  if (server !== null) {
    const apparaten = await pushApi.apparaten(browserAbo.endpoint);
    const eigen = apparaten.find((a) => a.dit_apparaat);
    if (!eigen) {
      console.info("push.herstel_overgeslagen", "ander_account");
      return { uitkomst: "overgeslagen", waarom: "ander_account" };
    }
    verlopen = Boolean(eigen.verlopen_op);
  }

  const sleutel = zelfdeSleutel(browserAbo, config.publieke_sleutel);
  let reden: HerstelReden | null = null;
  if (sleutel === false) reden = "sleutel_gewisseld";
  else if (server === null) reden = "onbekend_bij_server";
  else if (verlopen) reden = "verlopen_bij_server";
  if (reden === null) return { uitkomst: "in_orde" };

  // Voorkeuren bewaren vóór de browser iets afmeldt.
  const voorkeuren = server?.voorkeuren ?? STANDAARD_VOORKEUREN;
  const { abonnement, oudEndpoint } = await abonneerInBrowser(
    config.publieke_sleutel,
    {
      vraagToestemming: false,
      // Verlopen = het endpoint is dood, ook als de sleutel klopt. Onbekend
      // met een kloppende sleutel = alleen opnieuw registreren.
      vervang: reden === "verlopen_bij_server",
    },
  );
  await pushApi.abonneer(naarAbonnementInvoer(abonnement, voorkeuren));
  // Pas nu de oude serverrij weg: mislukte subscribe of registratie laat
  // hem met voorkeuren staan.
  await meldOudEndpointAf(oudEndpoint);
  return { uitkomst: "hersteld", reden };
}

let lopendHerstel: Promise<HerstelUitkomst> | null = null;
let volgendHerstel: Promise<HerstelUitkomst> | null = null;

/** Alleen voor tests: vergeet lopende en wachtende herstelruns. */
export function _resetHerstelSlot(): void {
  lopendHerstel = null;
  volgendHerstel = null;
}

/**
 * Zelfherstel na het inloggen (sessie 202610234).
 *
 * Alleen als de gebruiker al toestemming gaf én de browser een abonnement
 * heeft: wijkt de sleutel af van de huidige publieke sleutel, of kent de
 * server dit endpoint niet (meer) of als verlopen, dan schrijven we dit
 * toestel opnieuw in met de voorkeuren die er al waren. Hoort het endpoint
 * bij een ander account, dan doet hij niets. Vraagt nooit om toestemming.
 * Fouten gooit hij door; de hook logt ze stil.
 *
 * Nooit twee runs tegelijk: loopt er al een, dan wacht een nieuwe aanvraag
 * daarop en draait daarna nog één keer (vaker aanvragen voegt niets toe).
 */
export function herstelPushAbonnement(): Promise<HerstelUitkomst> {
  if (!lopendHerstel) {
    const run: Promise<HerstelUitkomst> = herstelEenKeer().finally(() => {
      if (lopendHerstel === run) lopendHerstel = null;
    });
    lopendHerstel = run;
    return run;
  }
  if (!volgendHerstel) {
    const vorige = lopendHerstel;
    const volgende: Promise<HerstelUitkomst> = vorige
      .catch(() => undefined)
      .then(() => {
        if (volgendHerstel === volgende) volgendHerstel = null;
        return herstelPushAbonnement();
      });
    volgendHerstel = volgende;
  }
  return volgendHerstel;
}

/** Vertaal een browser-abonnement naar de invoer van onze API. */
export function naarAbonnementInvoer(
  abonnement: PushSubscription,
  voorkeuren: Partial<Record<PushMeldingSoort, boolean>>,
): PushAbonnementInvoer {
  const json = abonnement.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error("abonnement_onvolledig");
  }
  return {
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
    voorkeuren,
    apparaat: apparaatLabel(),
  };
}
